import type { CustomerTypeId } from './customer-types';

export interface IntakeTemplate {
  template_id: string;
  label: string;
  systems: Array<{ system_id: string; variant_id: string; quantity_default?: number }>;
  project_type?: string;
  building_type?: string;
}

export type IntakeFocus =
  | 'algemeen'
  | 'badkamer'
  | 'keuken'
  | 'dak'
  | 'installaties'
  | 'verduurzaming'
  | 'transformatie'
  | 'onderhoud'
  | 'compleet';

export type IntakeScope = 'klein' | 'middel' | 'groot' | 'portefeuille';

export interface IntakeAnswers {
  customerTypeId: CustomerTypeId | string;
  projectTypeId: string;
  buildingTypeId: string;
  focus: IntakeFocus;
  scope: IntakeScope;
}

export interface RankedTemplate {
  template: IntakeTemplate;
  score: number;
  reasons: string[];
}

export type MatchConfidence = 'hoog' | 'middel' | 'laag';

const FOCUS_KEYWORDS: Record<IntakeFocus, string[]> = {
  algemeen: [],
  badkamer: ['badkamer', 'sanitair'],
  keuken: ['keuken'],
  dak: ['dak', 'dakisolatie'],
  installaties: ['installatie', 'cv', 'ventilatie', 'warmtepomp', 'pv', 'sanitair'],
  verduurzaming: ['verduurzaming', 'energielabel', 'gasloos', 'isolatie', 'warmtepomp', 'pv'],
  transformatie: ['transformatie', 'mixed', 'kantoor'],
  onderhoud: ['onderhoud', 'reserveberekening', 'meerjarig'],
  compleet: ['projectbegroting', 'volledige', 'compleet', 'mix'],
};

function keywordsForTemplate(t: IntakeTemplate): string {
  const systems = (t.systems ?? [])
    .map((s) => `${s.system_id} ${s.variant_id ?? ''}`.trim())
    .join(' ');
  return `${t.template_id} ${t.label} ${systems}`.toLowerCase();
}

function containsAny(haystack: string, needles: string[]): boolean {
  return needles.some((n) => haystack.includes(n));
}

function customerAffinityScore(customerTypeId: string, text: string): { score: number; reason?: string } {
  switch (customerTypeId) {
    case 'particulier':
    case 'huizenflipper':
      if (containsAny(text, ['badkamer', 'keuken', 'dak', 'gasloos', 'installatierenovatie'])) {
        return { score: 14, reason: 'Past bij particuliere renovatiescope.' };
      }
      return { score: 0 };
    case 'aannemer':
    case 'werkvoorbereider':
    case 'adviseur':
      if (containsAny(text, ['projectbegroting', 'volledige', 'mixed', 'transformatie', 'compleet'])) {
        return { score: 18, reason: 'Past bij professioneel detailniveau.' };
      }
      return { score: 8, reason: 'Werkbaar als technische basisvariant.' };
    case 'woningcorporatie':
    case 'vve':
      if (containsAny(text, ['vve', 'onderhoud', 'reserveberekening', 'meerjarig', 'zorg'])) {
        return { score: 20, reason: 'Past bij beheer/portefeuillecontext.' };
      }
      return { score: 4 };
    case 'overheid':
    case 'bank_financier':
    case 'projectontwikkelaar':
    case 'vastgoedontwikkelaar':
      if (containsAny(text, ['gebiedsontwikkeling', 'vergunning', 'projectbegroting', 'transformatie'])) {
        return { score: 16, reason: 'Past bij dossier-/investeringscontext.' };
      }
      return { score: 6 };
    default:
      return { score: 6, reason: 'Neutrale template-fit.' };
  }
}

export function resolveIntakeTemplates(
  templates: IntakeTemplate[],
  answers: IntakeAnswers
): RankedTemplate[] {
  const projectMatched = templates.filter((t) => (t.project_type ?? '').trim() === answers.projectTypeId);
  if (projectMatched.length === 0) return [];

  const buildingMatched = projectMatched.filter((t) => (t.building_type ?? '').trim() === answers.buildingTypeId);
  const pool = buildingMatched.length > 0 ? buildingMatched : projectMatched;

  const focusWords = FOCUS_KEYWORDS[answers.focus] ?? [];

  const ranked = pool.map((template) => {
    let score = 100; // project_type is already a hard match
    const reasons: string[] = ['Projecttype klopt met de intake.'];

    if ((template.building_type ?? '').trim() === answers.buildingTypeId) {
      score += 25;
      reasons.push('Gebouwtype sluit aan.');
    }

    const kw = keywordsForTemplate(template);
    if (focusWords.length > 0 && containsAny(kw, focusWords)) {
      score += 20;
      reasons.push('Focus van de renovatie komt overeen.');
    }

    const c = customerAffinityScore(answers.customerTypeId, kw);
    score += c.score;
    if (c.reason) reasons.push(c.reason);

    const systemsCount = template.systems.length;
    if (answers.scope === 'klein' && systemsCount <= 2) score += 8;
    if (answers.scope === 'middel' && systemsCount >= 2 && systemsCount <= 4) score += 10;
    if (answers.scope === 'groot' && systemsCount >= 4) score += 12;
    if (answers.scope === 'portefeuille' && containsAny(kw, ['vve', 'projectbegroting', 'gebiedsontwikkeling'])) {
      score += 16;
      reasons.push('Geschikt voor portefeuille/meerprojecten.');
    }

    return { template, score, reasons };
  });

  ranked.sort((a, b) => b.score - a.score || a.template.label.localeCompare(b.template.label, 'nl'));
  return ranked;
}

export function scopeMultiplier(scope: IntakeScope): number {
  if (scope === 'klein') return 0.8;
  if (scope === 'groot') return 1.25;
  if (scope === 'portefeuille') return 1.6;
  return 1;
}

export function confidenceFromScore(score: number): MatchConfidence {
  if (score >= 150) return 'hoog';
  if (score >= 120) return 'middel';
  return 'laag';
}
